import { ChangeEvent, Fragment, useState, useCallback, useMemo } from 'react';
import { debounce } from 'throttle-debounce';
import { useTranslation } from 'react-i18next';
// Types
import CaseStatuses from 'app/types/CaseStatuses';
import UserRoles from 'app/types/UserRoles';
// Models
import IOption from 'app/models/Option';
// Redux
import { useAppSelector } from 'app/hooks/useStore';
// Selectors
import { selectCurrentAccount, selectInvoicingEnabled } from 'app/store/Accounts/Accounts.selectors';
import { selectAccountsAsOptions } from 'app/store/Clients/Clients.selectors';
import { selectMyUser } from 'app/store/Users/Users.selectors';
// Mui
import { Box, Tooltip, MenuItem, Typography, Autocomplete, TextField } from '@mui/material';
// Icons
import { Tune as TuneIcon } from '@mui/icons-material';
// Components
import { IconButton, Input, Select } from 'app/components/Mui';
// Hooks
import useToggle from 'app/hooks/useToggle';
// Service
import LocalStorageService from 'app/services/LocalStorage.service';
// Utils
import useCasesContext from './utils/useCasesContext';
// 
import FilterPreferencesDialog from './FilterPreferencesDialog';

const CasesFilter = () => {
  const { t } = useTranslation();
  // State
  const myUser = useAppSelector(selectMyUser);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const invoicingEnabled = useAppSelector(selectInvoicingEnabled);
  const clientAccounts = useAppSelector(selectAccountsAsOptions)?.map(a => ({ id: a.value, name: a.label }));

  const { open, toggle } = useToggle();

  const { params, onChangeParams } = useCasesContext();

  // eslint-disable-next-line
  const debounceParams = useCallback(debounce(500, (name:string, value:any) => {
    const updatedParams = { ...params, offset: 0, [name]: value };

    if ( currentAccount && (name === 'sort' || name === 'sort.priority.collectionIds') ){
      LocalStorageService.setCasesSorting(currentAccount.id, {
        'sort': updatedParams['sort'],
        'sort.priority.collectionIds': updatedParams['sort.priority.collectionIds']
      });
    }

    onChangeParams(updatedParams);
    // eslint-disable-next-line
  }), [currentAccount, params]);

  const [ search, setSearch ] = useState('');

  const handleChange = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if ( name === 'search' ) setSearch(value);
    debounceParams(name, value);
  }

  const handleChangeClientAccount = (_:any, value:any) => {
    const updatedParams = { ...params, offset: 0, clientAccountId: value?.id || '' };
    onChangeParams(updatedParams);
  }

  const clientAccoutValue = useMemo(() => {
    return clientAccounts?.find(acc => acc.id === params['clientAccountId']) || null
  }, [params, clientAccounts]);

  const handleClearSearch = () => {
    const updatedParams = { ...params, search: '' };
    setSearch('');
    onChangeParams(updatedParams);
  }

  const handleClose = (nextCollectionIds?:string) => {
    if ( typeof nextCollectionIds !== 'undefined' ){
      debounceParams('sort.priority.collectionIds', nextCollectionIds);
    }
    toggle();
  }

  const sortOptions:IOption[] = [
    {
      id: 'document.createdOn desc',
      name: t('labels.latestDocumentNewToOld'),
    },
    {
      id: 'createdOn desc',
      name: t('labels.creationDateNewToOld'),
    },
    {
      id: 'createdOn asc',
      name: t('labels.creationDateOldToNew'),
    }
  ];

  const isSortingShown = params.status !== CaseStatuses.Waiting;
  const isMyUserRoleStaff = myUser?.role === UserRoles.Staff;

  return (
    <Fragment>
      <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
        <Input
          label={t('pages.cases.searchLabel')} id="search" name="search" value={search}
          fullWidth size="small" margin="none"
          onChange={handleChange}
          onClear={handleClearSearch}
        />
        {clientAccounts?.length && !isMyUserRoleStaff ? (
          <Autocomplete
            fullWidth
            sx={{ maxWidth: 320 }}
            options={clientAccounts}
            onChange={handleChangeClientAccount}
            value={clientAccoutValue}
            getOptionLabel={(option) => option.name}
            getOptionKey={(option) => option.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('pages.cases.clientAccountLabel')}
                size="small"
                margin="none"
              />
            )}
          />
        ) : null}

        {params.status !== CaseStatuses.Pending && params.status !== CaseStatuses.Rejected ? (
          <Fragment>
            {invoicingEnabled ? (
              <Select
                sx={{
                  maxWidth: 160
                }}
                label={t('pages.cases.invoiceStatusLabel')} id="invoice.status" name="invoice.status" value={params['invoice.status']}
                fullWidth size="small" margin="none"
                onChange={handleChange}
                options={[
                  { id: 'all', name: t('pages.cases.allOption') },
                  { id: 'draft', name: t('pages.cases.draftOption') },
                  { id: 'sent', name: t('pages.cases.sentOption') }
                ]}
                slotProps={{
                  inputLabel: {
                    shrink: true
                  },
                  select: {
                    displayEmpty: true
                  }
                }}
              />
            ) : null}
            {isSortingShown ? (
              <Fragment>
                <Select
                  label={t('labels.sortBy')} id="sort" name="sort" value={params['sort']}
                  fullWidth size="small" margin="none"
                  onChange={handleChange}
                  options={sortOptions}
                  renderOptions={(option:IOption, index:number) => (
                      <MenuItem
                        key={`option item ${index} ${option.id}`}
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          borderBottom: index % 2 === 1 && index !== sortOptions.length - 1 ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                        }}
                        value={option.id.toString()}
                      >
                        <Typography variant="subtitle1">{option.name}</Typography>
                        {/* <Typography sx={{ maxWidth: 320, whiteSpace: 'normal' }} variant="caption">{option.props.description}</Typography> */}
                      </MenuItem>
                  )}
                />
                <Tooltip title={t('pages.cases.sortPreferences')}>
                  <IconButton
                    name="Settings"
                    onClick={toggle}
                  ><TuneIcon /></IconButton>
                </Tooltip>
              </Fragment>
            ) : null}
          </Fragment>
        ) : null}
      </Box>
      {open ? (
        <FilterPreferencesDialog
          open={open}
          onClose={handleClose}
          params={params}
        />
      ) : null}
    </Fragment>
  )
}

export default CasesFilter;
